import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { H2, OL, P, UL } from './ui';

const post: Post = { meta: postMeta('how-producer-battles-work'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        A producer battle - sometimes called a beat battle - is a competition where music producers
        make original beats under a constraint, and the audience or judges pick a winner. The
        constraint is what makes it interesting: same sample kit, same BPM, same timer. Below is how
        a modern online producer battle actually runs, end to end.
      </P>

      <H2 id="phases">The four phases</H2>
      <OL>
        <li>
          <strong>Lobby.</strong> Producers join the match and confirm they are ready. The host (or
          the matchmaking system) locks in genre, BPM, and sample kit.
        </li>
        <li>
          <strong>Build.</strong> Timer starts. Everyone produces in their DAW with the same kit.
          Real-time matches typically run 15 to 25 minutes; async matches run a day to a week.
        </li>
        <li>
          <strong>Submit.</strong> Each producer uploads their beat. Submissions automatically close
          at the timer. Late uploads are rejected.
        </li>
        <li>
          <strong>Vote.</strong> Audience and/or judges score each beat blind. Producer identities
          stay hidden until results.
        </li>
      </OL>

      <H2 id="kit">The sample kit constraint</H2>
      <P>
        The defining feature of a modern beat battle is the shared sample kit. Every producer in a
        match gets the same drums, melodies, and one-shots at the same instant. This levels the
        playing field: it is no longer about who has the better library or the better hardware, it
        is about who uses the kit most creatively. Some battles allow you to layer your own sounds
        on top; others lock the kit completely.
      </P>

      <H2 id="originality">Originality rules</H2>
      <UL>
        <li>The beat must be made during the round, not pre-made and uploaded.</li>
        <li>No copyrighted samples or full vocal hooks from existing tracks.</li>
        <li>Vocal one-shots from the kit are allowed. Full verses are not.</li>
        <li>Re-uploads of someone else's beat are an instant disqualification.</li>
      </UL>
      <P>
        Manual reports go through a moderation queue. The platform also runs an automatic vote-ring
        scanner that flags mutual high-weight voting between the same accounts inside a short window
        - a common shape for both rigged voting and beat-trading.
      </P>

      <H2 id="genre-rules">Genre-specific rules</H2>
      <P>Each genre has its own conventions baked into the matchmaker:</P>
      <UL>
        <li>
          <strong>Trap</strong>: 130-160 BPM, 808 + hat triplets expected, vocal chops common.
        </li>
        <li>
          <strong>House</strong>: 120-128 BPM, four-on-the-floor required, off-beat hats.
        </li>
        <li>
          <strong>Drum and bass</strong>: 170-176 BPM, breakbeat or rolling drum patterns, sub-bass
          dominant.
        </li>
        <li>
          <strong>Lo-fi</strong>: 70-90 BPM, intentional saturation/dust, sample-flip friendly.
        </li>
        <li>
          <strong>Phonk</strong>: 130-150 BPM, distorted 808s, cowbell, Memphis-style vocal chops.
        </li>
      </UL>

      <H2 id="voting">Voting and scoring</H2>
      <P>
        Audience votes use a 1-to-5 scale per submission. Voters do not see producer names. Some
        platforms weight votes from established (high-honor) accounts more than guest votes to
        reduce sock-puppet manipulation.
      </P>

      <H2 id="ranking">How rankings update</H2>
      <P>Ranked play uses an ELO-style algorithm:</P>
      <UL>
        <li>Each producer has a rating per genre.</li>
        <li>Beating a higher-rated opponent earns more points than beating a lower one.</li>
        <li>Losing to a lower-rated opponent loses more points than losing to a higher one.</li>
        <li>After ~10 ranked matches your rating stabilizes and you get a tier.</li>
      </UL>

      <H2 id="dq">What gets you disqualified</H2>
      <UL>
        <li>Submitting a pre-made beat or someone else's beat (caught via moderation reports)</li>
        <li>Sample theft from copyrighted material</li>
        <li>Vote manipulation (sock puppets, vote-trading)</li>
        <li>Harassment or hate speech in chat or comments</li>
      </UL>

      <H2 id="tiebreakers">Tiebreakers</H2>
      <P>If two beats tie on votes, the tiebreaker uses (in order):</P>
      <OL>
        <li>Number of 5-star votes</li>
        <li>Number of unique voters</li>
        <li>Earliest submission timestamp</li>
      </OL>

      <H2 id="beyond">Beyond the match: tournaments, scouting, and friends</H2>
      <P>
        A single battle is the core loop, but the platform keeps going after results. Weekly
        showcases and bracket tournaments let you compete over days instead of minutes. An A&R layer
        lets label scouts pick standout tracks, co-sign producers, and post briefs ("make me a beat
        like this") - so a strong submission can get you scouted, not just ranked. Producers climb a
        Most Scouted board based on how many scouts back their work.
      </P>
      <P>
        It is also social: every submission stays on your public profile as a track listing, you can
        add other producers as friends (they approve the request first), and an inbox collects
        friend and A&R contact requests in one place. You control what is visible and which of these
        trigger an email from your settings.
      </P>

      <H2 id="ready">Ready to play?</H2>
      <P>
        Sign up free at{' '}
        <Link to="/" className="underline decoration-primary/50 underline-offset-2">
          Prod Battle
        </Link>{' '}
        and you are in your first match in a minute. Read{' '}
        <Link
          to="/blog/$slug"
          params={{ slug: 'how-to-win-a-beat-battle' }}
          className="underline decoration-primary/50 underline-offset-2"
        >
          how to win a beat battle
        </Link>{' '}
        before your first ranked match.
      </P>
    </>
  );
}
